import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { getStoredProfile } from "@/lib/auth";
import {
  X, Loader2, CheckCircle2, Calendar, Clock, MapPin, User, Phone, Mail,
  Home, FileText, Youtube, Package, ChevronLeft, ClipboardCheck, AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCENTS = {
  digimitra: "#4F46E5",
  youtuber: "#E11D48",
  bundle: "#9333EA",
};

const SERVICE_TYPES = [
  { value: "onboarding", label: "Onboarding (smartphone/app setup)" },
  { value: "troubleshooting", label: "Troubleshooting" },
  { value: "training", label: "Training" },
  { value: "concierge", label: "Concierge (banking, shopping, etc.)" },
];

const BUNDLES = [
  { value: 10, label: "10 Visits — ₹6,792", sub: "Save ₹198 (early-bird, GST incl.)" },
  { value: 20, label: "20 Visits — ₹11,985", sub: "Save ₹3,995 (early-bird, GST incl.)" },
];

function formatHourLabel(h) {
  if (h < 12) return `${h}:00 AM`;
  if (h === 12) return "12:00 PM";
  return `${h - 12}:00 PM`;
}

const TIME_SLOTS = Array.from({ length: 13 }, (_, i) => {
  const h = 8 + i;
  return { hour: h, label: formatHourLabel(h) };
});

const STEP_LABELS = {
  digimitra: ["Your Details", "Visit Details", "Schedule & Confirm"],
  youtuber: ["Your Details", "Program Details", "Schedule & Confirm"],
  bundle: ["Your Details", "Choose Bundle", "Review & Confirm"],
};

function BookingSuccess({ success, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-0 md:p-4" data-testid="elderra-success">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", damping: 22, stiffness: 220 }}
        className="bg-white rounded-t-[32px] md:rounded-[24px] w-full md:max-w-md p-7 text-center shadow-[0_20px_60px_rgba(45,39,102,0.15)]"
      >
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-3" strokeWidth={1.5} />
        <h3 className="text-2xl font-semibold text-[#1A1A1A] mb-1.5" data-testid="elderra-success-title">{success.title}</h3>
        <p className="text-lg text-[#4A4A4A] mb-3 leading-relaxed">{success.line}</p>
        {success.pay && <p className="text-base bg-amber-50 text-amber-800 rounded-xl p-3 mb-3">{success.pay}</p>}
        {success.ref && <p className="text-sm text-gray-400 font-mono mb-4" data-testid="elderra-success-ref">Ref: {success.ref}</p>}
        <button
          onClick={onClose}
          className="w-full min-h-[60px] bg-[#2D2766] text-white rounded-2xl font-semibold text-lg hover:bg-[#3B3382] active:scale-[0.98] transition-all"
          data-testid="elderra-success-close"
        >Done</button>
      </motion.div>
    </div>
  );
}

function StepProgress({ step, total, labels, accent }) {
  return (
    <div className="mb-6 pb-4 border-b border-gray-100" data-testid="booking-progress">
      <p className="text-base font-semibold text-[#4A4A4A] mb-2.5">
        Step {step} of {total}: <span style={{ color: accent }}>{labels[step - 1]}</span>
      </p>
      <div className="flex gap-1.5">
        {labels.map((_, i) => (
          <div
            key={i}
            className="h-2.5 flex-1 rounded-full transition-colors duration-300 bg-gray-200"
            style={i < step ? { backgroundColor: accent } : {}}
          />
        ))}
      </div>
    </div>
  );
}

function FieldError({ message }) {
  if (!message) return null;
  return (
    <p className="flex items-center gap-1.5 text-sm font-medium text-rose-600 mt-1.5" data-testid="field-error">
      <AlertCircle className="w-4 h-4 shrink-0" /> {message}
    </p>
  );
}

async function submitDigimitraBooking({ sessionId, name, phone, email, city, address, serviceType, date, slotHour, notes }) {
  const slot = TIME_SLOTS.find((s) => s.hour === Number(slotHour));
  const res = await axios.post(`${API}/elderra/digimitra/book`, {
    session_id: sessionId,
    customer_name: name,
    customer_phone: phone,
    customer_email: email.trim() || "",
    city,
    address,
    service_type: serviceType,
    preferred_date: date,
    preferred_time: slot.label,
    slot_date: date,
    slot_hour: Number(slotHour),
    additional_notes: notes,
  });
  return {
    title: "DigiMitra visit confirmed!",
    line: `Your ${slot.label} slot on ${date} in ${city} is booked.`,
    ref: res.data.agent_booking_ref,
    pay: "Pay ₹499/hour in cash to the DigiMitra after the visit.",
  };
}

async function submitYoutuberSignup({ sessionId, name, phone, email, city, youtubeInterest, address, date, slotHour, notes }) {
  const slot = TIME_SLOTS.find((s) => s.hour === Number(slotHour));
  const res = await axios.post(`${API}/elderra/youtuber/signup`, {
    session_id: sessionId,
    customer_name: name,
    customer_phone: phone,
    customer_email: email,
    city,
    youtube_interest: youtubeInterest,
    address,
    preferred_date: date,
    preferred_time: slot.label,
    slot_date: date,
    slot_hour: Number(slotHour),
    additional_notes: notes,
  });
  return {
    title: "YouTuber signup received!",
    line: "Our team will call you to schedule the first visit.",
    ref: res.data.agent_booking_ref,
    pay: "₹799 per visit.",
  };
}

async function submitBundleOrder({ sessionId, name, phone, email, bundleType }) {
  const res = await axios.post(`${API}/elderra/bundle/create-order`, {
    session_id: sessionId,
    customer_name: name,
    customer_phone: phone,
    customer_email: email,
    bundle_size: Number(bundleType),
    return_url: window.location.origin,
  });
  const link = res.data.payment_link || res.data.checkout_url
    || (res.data.payment_session_id ? `https://payments.cashfree.com/order/#${res.data.payment_session_id}` : null);
  if (!link) {
    toast.error("Could not open Cashfree checkout. Please try again.");
    return null;
  }
  window.open(link, "_blank");
  toast.info("Payment window opened");
  return {
    title: "Order created — finish payment",
    line: "Cashfree checkout opened in a new tab. Come back here after payment.",
    ref: res.data.agent_booking_ref,
    orderId: res.data.order_id,
  };
}

function extractErrorMessage(err) {
  const isCleanText = (s) => typeof s === "string" && s.trim() && s.length < 200 && !/<[^>]+>/.test(s);
  const detail = err.response?.data?.detail;
  if (isCleanText(detail)) return detail;
  if (detail && isCleanText(detail.body?.detail)) return detail.body.detail;
  if (Array.isArray(detail)) {
    const joined = detail.map((d) => d.msg || "").filter(Boolean).join(", ");
    if (isCleanText(joined)) return joined;
  }
  return "We couldn't complete this right now. Please try again in a moment.";
}

export default function ElderraBookingModal({ kind, sessionId, onClose, prefillProfile }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");

  // Prefill from the logged-in profile (or an explicitly passed one, e.g. a family
  // member booking on a senior's behalf) so seniors never re-enter known details.
  useEffect(() => {
    const p = prefillProfile || getStoredProfile();
    if (!p) return;
    if (p.name) setName(p.name);
    if (p.phone) setPhone(String(p.phone).slice(-10));
    if (p.city) setCity(p.city);
    if (p.address) setAddress(p.address);
  }, [prefillProfile]);

  const [serviceType, setServiceType] = useState("onboarding");
  const [youtubeInterest, setYoutubeInterest] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [slotHour, setSlotHour] = useState(10);

  const [bundleType, setBundleType] = useState(10);

  useEffect(() => {
    if (error) setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, phone, email, city, address, youtubeInterest, serviceType, bundleType, date, slotHour]);

  const accent = ACCENTS[kind];
  const labels = STEP_LABELS[kind];

  const titles = {
    digimitra: { title: "Book DigiMitra Home Visit", sub: "₹499/hour • Paid in cash after the visit", icon: Home },
    youtuber: { title: "YouTuber Program Signup", sub: "For 60+ seniors • ₹799 per visit", icon: Youtube },
    bundle: { title: "Buy a YouTuber Visit Bundle", sub: "Early-bird pricing, GST included", icon: Package },
  };
  const T = titles[kind];
  const Icon = T.icon;
  const inputCls = "w-full min-h-[56px] px-4 text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#2D2766]/20 focus:border-[#2D2766]";
  const labelCls = "flex items-center gap-1.5 text-lg font-semibold text-[#1A1A1A] mb-2";

  const validateStep1 = () => {
    if (!name.trim()) return "Please enter your name";
    if (!/^\d{10}$/.test(phone)) return "Please enter a valid 10-digit phone number";
    if (!email.trim()) return "Please enter your email";
    return null;
  };

  const validateStep2 = () => {
    if (kind === "digimitra") {
      if (!city.trim()) return "Please enter your city";
      if (!address.trim()) return "Please enter your address";
    }
    if (kind === "youtuber") {
      if (!city.trim()) return "Please enter your city";
      if (!youtubeInterest.trim()) return "Please tell us what kind of videos you'd like to make";
    }
    return null;
  };

  const goNext = () => {
    const err = step === 1 ? validateStep1() : validateStep2();
    if (err) { setError(err); return; }
    setError("");
    setStep((s) => s + 1);
  };

  const goBack = () => {
    setError("");
    setStep((s) => s - 1);
  };

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      const formState = { sessionId, name, phone, email, city, address, serviceType, youtubeInterest, date, slotHour, notes, bundleType };
      let result = null;
      if (kind === "digimitra") result = await submitDigimitraBooking(formState);
      else if (kind === "youtuber") result = await submitYoutuberSignup(formState);
      else if (kind === "bundle") result = await submitBundleOrder(formState);

      if (result) {
        setSuccess(result);
        toast.success(kind === "youtuber" ? "Signup received" : "Booking confirmed");
      }
    } catch (err) {
      const msg = extractErrorMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return <BookingSuccess success={success} onClose={onClose} />;
  }

  const stepVariants = {
    enter: { opacity: 0, x: 24 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -24 },
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 md:fixed md:inset-0 md:flex md:items-center md:justify-center md:bg-black/40 md:backdrop-blur-sm" data-testid={`elderra-modal-${kind}`}>
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        transition={{ type: "spring", damping: 26, stiffness: 220 }}
        className="bg-[#FDFBF7] rounded-t-[32px] md:rounded-[24px] w-full md:max-w-2xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto shadow-[0_-10px_40px_rgba(0,0,0,0.1)] md:shadow-[0_20px_60px_rgba(45,39,102,0.15)]"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-start justify-between z-10">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-2xl" style={{ backgroundColor: `${accent}1A` }}>
              <Icon className="w-6 h-6" style={{ color: accent }} strokeWidth={1.75} />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-[#1A1A1A]">{T.title}</h3>
              <p className="text-base text-[#4A4A4A] mt-0.5">{T.sub}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full shrink-0" data-testid="elderra-modal-close" aria-label="Close">
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        <div className="p-5 md:p-7">
          <StepProgress step={step} total={3} labels={labels} accent={accent} />

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="step1" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }} className="space-y-6" data-testid="booking-step-1">
                <div>
                  <label className={labelCls}><User className="w-5 h-5" style={{ color: accent }} />Full name</label>
                  <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="elderra-name" />
                </div>
                <div>
                  <label className={labelCls}><Phone className="w-5 h-5" style={{ color: accent }} />Phone number</label>
                  <input className={inputCls} value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10 digits" inputMode="numeric" data-testid="elderra-phone" />
                </div>
                <div>
                  <label className={labelCls}><Mail className="w-5 h-5" style={{ color: accent }} />Email</label>
                  <input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="elderra-email" />
                  {prefillProfile && (
                    <p className="text-sm text-gray-400 mt-1.5">Booking on {prefillProfile.name}'s behalf — you can enter your own email here.</p>
                  )}
                </div>
                <FieldError message={error} />
              </motion.div>
            )}

            {step === 2 && kind !== "bundle" && (
              <motion.div key="step2" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }} className="space-y-6" data-testid="booking-step-2">
                <div>
                  <label className={labelCls}><MapPin className="w-5 h-5" style={{ color: accent }} />City</label>
                  <input className={inputCls} value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Mumbai" data-testid="elderra-city" />
                </div>
                <div>
                  <label className={labelCls}><Home className="w-5 h-5" style={{ color: accent }} />Address {kind === "youtuber" ? "(optional)" : ""}</label>
                  <input className={inputCls} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Flat, street, area" data-testid="elderra-address" />
                </div>
                {kind === "digimitra" && (
                  <div>
                    <label className={labelCls}>Service needed</label>
                    <select className={inputCls} value={serviceType} onChange={(e) => setServiceType(e.target.value)} data-testid="elderra-service-type">
                      {SERVICE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                )}
                {kind === "youtuber" && (
                  <div>
                    <label className={labelCls}><Youtube className="w-5 h-5" style={{ color: accent }} />What kind of videos?</label>
                    <input
                      className={inputCls}
                      value={youtubeInterest}
                      onChange={(e) => setYoutubeInterest(e.target.value)}
                      placeholder="e.g. Cooking recipes, travel stories, bhajans"
                      data-testid="elderra-youtube-interest"
                    />
                  </div>
                )}
                <FieldError message={error} />
              </motion.div>
            )}

            {step === 2 && kind === "bundle" && (
              <motion.div key="step2-bundle" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }} className="space-y-4" data-testid="booking-step-2">
                <label className={labelCls}>Choose your bundle</label>
                <div className="space-y-3">
                  {BUNDLES.map((b) => (
                    <label
                      key={b.value}
                      className="block p-4 border-2 rounded-2xl cursor-pointer transition-all"
                      style={bundleType === b.value ? { borderColor: accent, backgroundColor: `${accent}0D` } : { borderColor: "#D1D5DB" }}
                      data-testid={`elderra-bundle-${b.value}`}
                    >
                      <input type="radio" className="sr-only" name="bundle" value={b.value} checked={bundleType === b.value} onChange={(e) => setBundleType(Number(e.target.value))} />
                      <div className="text-lg font-semibold text-[#1A1A1A]">{b.label}</div>
                      <div className="text-base text-[#4A4A4A] mt-0.5">{b.sub}</div>
                    </label>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="step3" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }} className="space-y-6" data-testid="booking-step-3">
                {kind !== "bundle" && (
                  <>
                    <div>
                      <label className={labelCls}><Calendar className="w-5 h-5" style={{ color: accent }} />Preferred date</label>
                      <input type="date" min={today} className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} data-testid="elderra-date" />
                    </div>
                    <div>
                      <label className={labelCls}><Clock className="w-5 h-5" style={{ color: accent }} />Preferred time</label>
                      <div className="grid grid-cols-3 gap-2.5" data-testid="elderra-slot-grid">
                        {TIME_SLOTS.map((s) => (
                          <button
                            type="button"
                            key={s.hour}
                            onClick={() => setSlotHour(s.hour)}
                            className="min-h-[56px] rounded-xl border-2 text-base font-medium transition-all active:scale-[0.98]"
                            style={Number(slotHour) === s.hour
                              ? { borderColor: accent, backgroundColor: `${accent}1A`, color: accent }
                              : { borderColor: "#D1D5DB", color: "#333333" }}
                            data-testid={`elderra-slot-${s.hour}`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}><FileText className="w-5 h-5" style={{ color: accent }} />Notes (optional)</label>
                      <textarea className={`${inputCls} py-3`} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Language preference, specific help needed..." data-testid="elderra-notes" />
                    </div>
                  </>
                )}

                {/* Review summary */}
                <div className="bg-white border-2 border-gray-200 rounded-2xl p-4" data-testid="booking-review-summary">
                  <p className="flex items-center gap-2 text-lg font-semibold text-[#1A1A1A] mb-3">
                    <ClipboardCheck className="w-5 h-5" style={{ color: accent }} /> Review your details
                  </p>
                  <div className="space-y-1.5 text-base text-[#333333]">
                    <p><span className="text-[#4A4A4A]">Name:</span> {name}</p>
                    <p><span className="text-[#4A4A4A]">Phone:</span> {phone}</p>
                    {email && <p><span className="text-[#4A4A4A]">Email:</span> {email}</p>}
                    {kind !== "bundle" && <p><span className="text-[#4A4A4A]">City:</span> {city}</p>}
                    {kind === "digimitra" && <p><span className="text-[#4A4A4A]">Address:</span> {address}</p>}
                    {kind === "youtuber" && youtubeInterest && <p><span className="text-[#4A4A4A]">Videos:</span> {youtubeInterest}</p>}
                    {kind === "bundle" && <p><span className="text-[#4A4A4A]">Bundle:</span> {BUNDLES.find((b) => b.value === Number(bundleType))?.label}</p>}
                  </div>
                </div>
                <FieldError message={error} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Sticky bottom nav */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-5 py-4 flex items-center gap-3">
          {step > 1 && (
            <button
              type="button"
              onClick={goBack}
              disabled={loading}
              className="flex items-center gap-1 min-h-[60px] px-4 font-bold text-lg rounded-2xl disabled:opacity-50"
              style={{ color: accent }}
              data-testid="btn-back-step"
            >
              <ChevronLeft className="w-5 h-5" /> Back
            </button>
          )}
          {step < 3 ? (
            <button
              type="button"
              onClick={goNext}
              className="flex-1 min-h-[60px] text-white rounded-2xl font-semibold text-lg active:scale-[0.98] transition-all"
              style={{ backgroundColor: accent }}
              data-testid="btn-next-step"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={loading}
              className="flex-1 min-h-[60px] text-white rounded-2xl font-semibold text-lg disabled:opacity-60 flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
              style={{ backgroundColor: accent }}
              data-testid="elderra-submit"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              {kind === "bundle" ? "Create order & pay" : kind === "youtuber" ? "Sign me up" : "Confirm booking"}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
